import type { Attachment, SendAttachment } from './api'

const STORAGE_PREFIX = 'agentchat-pending-attachments:v1:'

export type PersistedPendingAttachment = {
  clientId: string
  filename: string
  kind: Attachment['kind']
  mimeType: string | null
  sizeBytes: number | null
  uploadedId: string
  localPath: string
}

export type PersistablePendingAttachment = {
  clientId: string
  filename: string
  kind: SendAttachment['kind']
  mimeType: string | null
  sizeBytes: number | null
  uploadedId: string | null
  localPath: string | null
  status: string
}

export function pendingAttachmentsStorageKey(draftKey: string): string {
  return `${STORAGE_PREFIX}${draftKey}`
}

function browserStorage(): Storage | null {
  if (typeof window === 'undefined') return null
  try {
    return window.localStorage
  } catch {
    return null
  }
}

function validPendingAttachment(value: unknown): PersistedPendingAttachment | null {
  if (!value || typeof value !== 'object') return null
  const raw = value as Record<string, unknown>
  if (typeof raw.clientId !== 'string' || !raw.clientId) return null
  if (typeof raw.filename !== 'string') return null
  if (typeof raw.uploadedId !== 'string' || !raw.uploadedId) return null
  if (typeof raw.localPath !== 'string' || !raw.localPath) return null
  const kind = raw.kind
  if (
    kind !== 'image' &&
    kind !== 'video' &&
    kind !== 'audio' &&
    kind !== 'voice' &&
    kind !== 'document' &&
    kind !== 'pdf'
  ) {
    return null
  }
  return {
    clientId: raw.clientId,
    filename: raw.filename,
    kind,
    mimeType: typeof raw.mimeType === 'string' ? raw.mimeType : null,
    sizeBytes: typeof raw.sizeBytes === 'number' ? raw.sizeBytes : null,
    uploadedId: raw.uploadedId,
    localPath: raw.localPath,
  }
}

export function loadPersistedPendingAttachments(draftKey: string): PersistedPendingAttachment[] {
  const storage = browserStorage()
  if (!storage) return []
  const raw = storage.getItem(pendingAttachmentsStorageKey(draftKey))
  if (!raw) return []
  try {
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed
      .map(validPendingAttachment)
      .filter((att): att is PersistedPendingAttachment => att !== null)
  } catch {
    return []
  }
}

export function persistablePendingAttachments(
  attachments: PersistablePendingAttachment[],
): PersistedPendingAttachment[] {
  return attachments
    .filter((att) => att.status === 'uploaded' && !!att.uploadedId && !!att.localPath)
    .map((att) => ({
      clientId: att.clientId,
      filename: att.filename,
      kind: att.kind,
      mimeType: att.mimeType,
      sizeBytes: att.sizeBytes,
      uploadedId: att.uploadedId!,
      localPath: att.localPath!,
    }))
}

export function savePersistedPendingAttachments(
  draftKey: string,
  attachments: PersistablePendingAttachment[],
): void {
  const storage = browserStorage()
  if (!storage) return
  const persistable = persistablePendingAttachments(attachments)
  const key = pendingAttachmentsStorageKey(draftKey)
  if (persistable.length === 0) {
    storage.removeItem(key)
    return
  }
  storage.setItem(key, JSON.stringify(persistable))
}

export function clearPersistedPendingAttachments(draftKey: string): void {
  const storage = browserStorage()
  if (!storage) return
  storage.removeItem(pendingAttachmentsStorageKey(draftKey))
}
